import type { Namespace } from '../../../../types/namespace';
import type { Flows, Folder } from '../../../../util/Types/Folder';
import type { FluxObject } from '../AgentsSidebar/context';

export type SidebarChild =
  | { kind: 'folder'; id: string; rank: string; index: number; node: Folder }
  | { kind: 'flow'; id: string; rank: string; index: number; node: Flows }
  | { kind: 'form'; id: string; rank: string; index: number; node: Flows }
  | {
      kind: 'object';
      id: string;
      rank: string;
      index: number;
      node: FluxObject;
    }
  | {
      kind: 'vector';
      id: string;
      rank: string;
      index: number;
      node: Namespace;
    };

interface BuildArgs {
  subfolders?: Folder[];
  flows?: Flows[];
  objects?: FluxObject[];
  namespaces?: Namespace[];
}

const rankOf = (item: { rank?: string | null }): string => item.rank ?? '';

const compareChildren = (a: SidebarChild, b: SidebarChild): number => {
  if (a.rank && b.rank) {
    if (a.rank < b.rank) return -1;
    if (a.rank > b.rank) return 1;
    return 0;
  }
  if (a.rank) return -1;
  if (b.rank) return 1;
  return a.index - b.index;
};

export const buildSidebarChildren = ({
  subfolders = [],
  flows = [],
  objects = [],
  namespaces = [],
}: BuildArgs): SidebarChild[] => {
  const children: SidebarChild[] = [];
  for (const f of subfolders) {
    children.push({
      kind: 'folder',
      id: f.id,
      rank: rankOf(f),
      index: f.index ?? 0,
      node: f,
    });
  }
  for (const f of flows) {
    children.push({
      kind: f.isForm ? 'form' : 'flow',
      id: f.id,
      rank: rankOf(f as { rank?: string | null }),
      index: f.index ?? 0,
      node: f,
    });
  }
  for (const o of objects) {
    children.push({
      kind: 'object',
      id: o.id,
      rank: rankOf(o as { rank?: string | null }),
      index: o.index ?? 0,
      node: o,
    });
  }
  for (const n of namespaces) {
    children.push({
      kind: 'vector',
      id: n.id,
      rank: rankOf(n as { rank?: string | null }),
      index: n.index ?? 0,
      node: n,
    });
  }
  return children.sort(compareChildren);
};
